import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, Users } from 'lucide-react';
import { getApiUrl, getAssetUrl } from '../config/config';
import GoldCrown from '../components/GoldCrown';

const ExploreContainer = styled.div`min-height: 100vh; background: #000; padding: 60px 20px;`;
const ExploreHeader = styled.div`max-width: 1200px; margin: 0 auto 50px; text-align: center;`;
const ExploreTitle = styled.h1`font-size: 3.5rem; color: #fff; margin-bottom: 16px;`;

const SearchSection = styled.div`max-width: 700px; margin: 0 auto 50px; position: relative; display: flex; align-items: center; svg { position: absolute; left: 20px; color: #666; }`;
const SearchInput = styled.input`width: 100%; padding: 16px 20px 16px 50px; background: #111; border: 1px solid #333; border-radius: 12px; color: #fff; outline: none; &:focus { border-color: #e0c16f; }`;

const CreatorsGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 30px; max-width: 1200px; margin: 0 auto;`;

const CreatorCard = styled(motion.div)`background: #111; border: 1px solid #333; border-radius: 20px; overflow: hidden; cursor: pointer; transition: 0.3s; &:hover { border-color: #e0c16f; transform: translateY(-8px); }`;
const CardBanner = styled.div`height: 120px; background: #222; position: relative; img { width: 100%; height: 100%; object-fit: cover; }`;
const CardAvatarContainer = styled.div`width: 90px; height: 90px; border-radius: 50%; border: 4px solid #111; position: absolute; bottom: -45px; left: 50%; transform: translateX(-50%); background: #e0c16f; overflow: hidden;`;

const CardBody = styled.div`padding: 60px 24px 24px; text-align: center;`;
const CreatorNameWrapper = styled.div`display: flex; align-items: center; justify-content: center; gap: 0; margin-bottom: 4px;`;
const CreatorName = styled.h3`font-size: 1.4rem; color: #fff; margin: 0;`;

const ExploreCreators = () => {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        setLoading(true);
        setError('');
        const query = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
        const response = await fetch(`${getApiUrl()}/creators${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Unable to load creators');
        if (active) setCreators(data.creators || []);
      } catch (requestError) {
        if (active) {
          setCreators([]);
          setError(requestError.message);
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => { active = false; window.clearTimeout(timer); };
  }, [search]);

  return (
    <ExploreContainer>
      <ExploreHeader>
        <ExploreTitle>Explore Creators</ExploreTitle>
        <SearchSection>
          <Search size={20} />
          <SearchInput placeholder="Search creators..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </SearchSection>
      </ExploreHeader>

      {loading ? <div style={{textAlign: 'center', color: '#e0c16f'}}>Discovering...</div> : error ? (
        <div style={{textAlign: 'center', color: '#ff8b96'}}>Creators could not be loaded. Please try again.</div>
      ) : creators.length === 0 ? (
        <div style={{textAlign: 'center', color: '#888'}}>No creators found.</div>
      ) : (
        <CreatorsGrid>
          {creators.map((creator, index) => (
            <CreatorCard key={creator.id} onClick={() => navigate(`/${creator.username}`)} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <CardBanner>
                {(creator.banner_image || creator.banner_url) && <img src={getAssetUrl(creator.banner_image || creator.banner_url)} alt={`${creator.display_name || creator.username} banner`} />}
                <CardAvatarContainer>
                    {(creator.profile_image || creator.avatar_url) ? <img src={getAssetUrl(creator.profile_image || creator.avatar_url)} alt={`${creator.display_name || creator.username} avatar`} style={{width: '100%', height: '100%', objectFit:'cover'}} /> : <div style={{height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'#000', fontSize:'2rem', fontWeight:800}}>{(creator.display_name || creator.username || '?').charAt(0).toUpperCase()}</div>}
                </CardAvatarContainer>
              </CardBanner>
              <CardBody>
                <CreatorNameWrapper>
                  <CreatorName>{creator.display_name || creator.username}</CreatorName>
                  {/* SADA IDENTIČNA RAZDALJINA KAO NA PROFILU */}
                  {Number(creator.is_gold) === 1 && <div style={{marginLeft: '-12px', marginTop: '-18px'}}><GoldCrown /></div>}
                </CreatorNameWrapper>
                <div style={{color: '#e0c16f', marginBottom: '16px'}}>@{creator.username}</div>
                <p style={{color: '#aaa', fontSize: '0.9rem', marginBottom: '20px'}}>{creator.bio || 'Check out my profile!'}</p>
                <div style={{display: 'flex', justifyContent: 'center', gap: '10px', color: '#888', borderTop: '1px solid #222', paddingTop: '15px'}}>
                   <Users size={18} /> {creator.total_supporters || 0} Fans
                </div>
              </CardBody>
            </CreatorCard>
          ))}
        </CreatorsGrid>
      )}
    </ExploreContainer>
  );
};

export default ExploreCreators;
